<script lang="ts">
	import type {
		InventoryQuestion,
		SearchField,
		InventoryQuestionValue,
		InventoryQuestionChoice,
		SearchFieldProps,
	} from 'utility/search-fields'

	import { v4 as uuid } from '@lukeed/uuid'
	import { onMount } from 'svelte'

	import Input, { type InputTypes } from '@isoftdata/svelte-input'
	import CurrencyInput from '@isoftdata/svelte-currency-input'
	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import Pin from 'components/search/Pin.svelte'
	import NullableRadioCheckbox from '../NullableRadioCheckbox.svelte'

	// I would extends from a base component, but this component extends multiple, so it's simplier not to.
	interface Props extends SearchFieldProps<InventoryQuestionValue> {
		id?: string
		value?: InventoryQuestionValue
	}

	let {
		searchField,
		inEditMode,
		pinnedSearchFields,
		selectedSearchFieldsKeys,
		value = $bindable(searchField?.qaFieldData?.dataType === 'BOOLEAN' ? true : ''),
		disabled = false,
		change,
		...rest
	}: Props = $props()

	let datalistId: string = uuid()
	const disableInput: boolean = $derived(disabled || inEditMode)
	const label = $derived(searchField?.qaFieldData?.name ?? '')

	const textInputTypeMap = new Map<InventoryQuestion['dataType'], InputTypes>([
		['TEXT', 'text'],
		['DATE', 'date'],
		['TIME', 'time'],
		['DATETIME', 'datetime-local'],
	])

	function currencyChangeHandler(newValue: string, searchField: SearchField) {
		value = newValue
		change?.({ field: searchField, value })
	}

	function autocompleteChangeHandler(choice: InventoryQuestionChoice | null, searchField: SearchField) {
		value = choice
		change?.({ field: searchField, value })
	}

	function elementChangeHandler(e: Event, searchField: SearchField) {
		const eventValue = (e.target as HTMLInputElement)?.value
		if (searchField?.qaFieldData?.dataType === 'NUMBER') {
			value = parseFloat(eventValue.replace(/[^\d.]/g, '')) //remove any non-numeric values
		} else {
			value = eventValue
		}
		change?.({ field: searchField, value })
	}

	//Type Guards
	function isString(qaField: InventoryQuestion, value: InventoryQuestionValue): value is string {
		return Array.from(textInputTypeMap.keys()).includes(qaField.dataType)
	}

	function isChoice(qaField: InventoryQuestion, value: InventoryQuestionValue): value is InventoryQuestionChoice {
		return ['CHOICE', 'AUTOSUGGEST_TEXT'].includes(qaField.dataType)
	}

	function isBoolean(qaField: InventoryQuestion, value: InventoryQuestionValue): value is boolean {
		return qaField.dataType === 'BOOLEAN'
	}

	function isCurrency(qaField: InventoryQuestion, value: InventoryQuestionValue): value is string {
		return qaField.dataType === 'CURRENCY'
	}

	function isNumber(qaField: InventoryQuestion, value: InventoryQuestionValue): value is number {
		return qaField.dataType === 'NUMBER'
	}

	onMount(() => {
		// TODO: remove this after I find where upstream is setting boolean Q&A values to true/false instead of null
		if (searchField?.qaFieldData && isBoolean(searchField?.qaFieldData, value)) {
			value = null
		}
	})
</script>

{#if searchField?.qaFieldData}
	{@const qaField = searchField?.qaFieldData}
	<EditModeControls
		{searchField}
		{inEditMode}
		{pinnedSearchFields}
		{selectedSearchFieldsKeys}
	>
		{#if isString(qaField, value)}
			<Input
				{...rest}
				{label}
				{value}
				type={textInputTypeMap.get(qaField.dataType) ?? 'text'}
				list={datalistId}
				disabled={disableInput}
				onchange={e => elementChangeHandler(e, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</Input>
		{:else if isNumber(qaField, value)}
			<!-- we can't use type="number" here because if they type in something non-numeric, the change event won't even fire -->
			<Input
				{...rest}
				{label}
				{value}
				list={datalistId}
				disabled={disableInput}
				inputmode="numeric"
				onchange={e => elementChangeHandler(e, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</Input>
		{:else if isChoice(qaField, value)}
			<Autocomplete
				{...rest}
				{label}
				{value}
				options={qaField.choices}
				emptyValue={null}
				getLabel={choice => choice?.label ?? ''}
				disabled={disableInput}
				change={option => autocompleteChangeHandler(option, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</Autocomplete>
		{:else if isCurrency(qaField, value)}
			<CurrencyInput
				{...rest}
				{label}
				{value}
				list={datalistId}
				disabled={disableInput}
				change={e => currencyChangeHandler(e.value, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</CurrencyInput>
		{:else if isBoolean(qaField, value)}
			<NullableRadioCheckbox
				id={searchField.key}
				label={searchField.name}
				disabled={disableInput}
				bind:value
				change={value => change?.({ field: searchField, value: value })}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</NullableRadioCheckbox>
		{/if}

		<!-- So if its not of these fields specifically mapped to an autocomplete and there are choices, just use a datalist element for the choices, I guess -->
		{#if qaField?.choices.length && !['AUTOSUGGEST_TEXT', 'CHOICE'].includes(qaField.dataType)}
			<datalist id={datalistId}>
				{#each qaField.choices as choice}
					<option value={choice.label}></option>
				{/each}
			</datalist>
		{/if}
	</EditModeControls>
{/if}
